import { CONSULTATION_STEPS, CONSULTATION_TYPE, MODULE_TYPE, User } from '@abiglobalhealth/go-types'
import { useContext } from 'react'

import { ConsultationContext, ConsultationContextProps } from '@/contexts/consultation'
import { useConsultation } from '@/hooks/useConsultation'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { getInitialStep } from '@/utils/step'

const NEXT_STEP: Record<CONSULTATION_STEPS, CONSULTATION_STEPS> = {
  [CONSULTATION_STEPS.LANDING]: CONSULTATION_STEPS.FORM,
  [CONSULTATION_STEPS.FORM]: CONSULTATION_STEPS.MATCHING,
  [CONSULTATION_STEPS.MATCHING]: CONSULTATION_STEPS.MATCH,
  [CONSULTATION_STEPS.MATCH]: CONSULTATION_STEPS.ANSWER,
  [CONSULTATION_STEPS.ANSWER]: CONSULTATION_STEPS.LANDING,
  [CONSULTATION_STEPS.INDICATION_SURVEY]: CONSULTATION_STEPS.FORM,
  [CONSULTATION_STEPS.ONGOING_VIDEO_CALL]: CONSULTATION_STEPS.VIDEO_CALL_ENDED,
  [CONSULTATION_STEPS.VIDEO_CALL_ENDED]: CONSULTATION_STEPS.ANSWER,
  [CONSULTATION_STEPS.WAITING_FOR_INFO]: CONSULTATION_STEPS.ANSWER,
  [CONSULTATION_STEPS.WAITING_FOR_SWAP]: CONSULTATION_STEPS.MATCHING,
  [CONSULTATION_STEPS.WAITING_FOR_CONSULTATION_PAYMENT]: CONSULTATION_STEPS.MATCHING,
  [CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER]: CONSULTATION_STEPS.MATCHING
}

interface UseConsultationStepProps {
  currentConsultationStep: ConsultationContextProps['currentConsultationStep']
  updateStep: (step: CONSULTATION_STEPS) => void
  nextStep: () => void
}

export const useConsultationStep = (): UseConsultationStepProps => {
  const { currentConsultationStep, setCurrentConsultationStep } = useContext(ConsultationContext)
  const { currentModule } = useModule()
  const { updateStep } = useStep()
  const { user } = useUser()
  const { addTrace, setTraces } = useTraces()
  const { isFollowUp } = useConsultation()

  const updateConsultationStep = (step: CONSULTATION_STEPS) => {
    setCurrentConsultationStep(step)
  }

  const nextStep = () => {
    switch (currentConsultationStep) {
      case CONSULTATION_STEPS.LANDING: {
        if (currentModule?.type !== MODULE_TYPE.CONSULTATION) return

        if (isFollowUp) return updateConsultationStep(CONSULTATION_STEPS.MATCHING)

        if (!currentModule.initialSteps.length) return updateConsultationStep(CONSULTATION_STEPS.MATCHING)

        // Check if there are form steps
        const initialStep = getInitialStep({
          module: currentModule,
          user: user as User
        })

        addTrace(currentModule.id, initialStep.id)
        updateStep(initialStep)
        updateConsultationStep(
          currentModule.consultationType === CONSULTATION_TYPE.PRESCRIPTION ? CONSULTATION_STEPS.INDICATION_SURVEY : CONSULTATION_STEPS.FORM
        )

        break
      }

      case CONSULTATION_STEPS.ANSWER: {
        return updateConsultationStep(isFollowUp ? CONSULTATION_STEPS.MATCHING : NEXT_STEP[currentConsultationStep])
      }

      case CONSULTATION_STEPS.FORM: {
        // Remove all form traces
        setTraces((prev) => prev.filter((trace) => trace.moduleId !== currentModule?.id || ('isFirstOfModule' in trace && trace.isFirstOfModule)))
        updateConsultationStep(NEXT_STEP[currentConsultationStep])
        break
      }

      default: {
        console.warn('nextStep: default case', currentConsultationStep)
        updateConsultationStep(CONSULTATION_STEPS.LANDING)
      }
    }
  }

  return {
    currentConsultationStep: currentModule?.type === MODULE_TYPE.PHARMACY_SELECTOR ? CONSULTATION_STEPS.INDICATION_SURVEY : currentConsultationStep,
    updateStep: updateConsultationStep,
    nextStep
  }
}
